import React, { useState, useEffect } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { v4 as uuidv4 } from "uuid";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import StarIcon from "@mui/icons-material/Star";
import AddJobModal from "./AddJobModal";
import { useAuth } from "../../AuthContext";
import ViewJobModal from "./ViewJobModal";
import EditJobModal from "./EditJobModal";
import KanbanBoardTwo from "./KanbanBoardTwo";
import "../../style/KanbanBoard.css";
import "../../style/KanbanResponsive.css"; // Add this new import

// Button component
const Button = ({ onClick, children }) => (
  <button
    onClick={onClick}
    className="bg-blue-500 text-white px-4 py-2 rounded"
  >
    {children}
  </button>
);

// Input component
const Input = ({ value, onChange, placeholder, name }) => (
  <input
    type="text"
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    className="border p-2 rounded w-full"
    name={name}
  />
);

// Search component
const SearchBar = ({ onSearch, searchResults, onResultClick }) => (
  <div className="search-container">
    <div className="flex items-center border rounded bg-white">
      <SearchIcon
        className="text-gray-400 ml-2"
        style={{ position: "absolute", left: "10px" }}
      />
      <input
        type="text"
        placeholder="Search by company or position..."
        onChange={(e) => onSearch(e.target.value)}
        className="p-2 w-full focus:outline-none pl-10" // Added pl-10 for icon space
      />
    </div>

    {searchResults.length > 0 && (
      <div className="search-results">
        {searchResults.map((result) => (
          <div key={result.id} onClick={() => onResultClick(result)}>
            <p className="font-semibold">{result.details.JobPosition}</p>
            <p className="text-sm text-gray-600">
              {result.details.CompanyName}
            </p>
          </div>
        ))}
      </div>
    )}
  </div>
);
const ItemType = "TASK";

const KanbanBoard = () => {
  const { userId } = useAuth();
  const API_BASE_URL = process.env.REACT_APP_API_SOURCE;

  const [columns, setColumns] = useState([
    { id: "saved_jobs", name: "Saved Jobs" },
    { id: "applied", name: "Applied" },
    { id: "interview", name: "Interview" },
    { id: "offered", name: "Offered" },
  ]);

  const [selectedTags, setSelectedTags] = useState([
    "saved_jobs",
    "applied",
    "interview",
    "offered",
  ]);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [customTags, setCustomTags] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [newColumn, setNewColumn] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewJobModalOpen, setViewJobModalOpen] = useState(false);
  const [editJobModalOpen, setEditJobModalOpen] = useState(false); // New state for edit modal
  const [currentColumnId, setCurrentColumnId] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [newTaskDetails, setNewTaskDetails] = useState({
    CompanyName: "",
    JobPosition: "",
    JobLocation: "",
    JobDescription: "",
    JobDate: "",
    JobTime: "",
    joblink: "",
    JobCategory: "saved_jobs",
  });

  useEffect(() => {
    if (userId) {
      fetchJobs();
      fetchTags(); // ✅ Load custom tags as columns
    }
  }, [userId]);

  // Ensure's that only logged in user's state of selected tags is displayed
  useEffect(() => {
    const savedTags = localStorage.getItem(`selectedTags-${userId}`);
    if (savedTags) {
      try {
        const parsed = JSON.parse(savedTags);
        if (Array.isArray(parsed)) {
          setSelectedTags(parsed);
        }
      } catch (e) {
        console.warn("Failed to parse selectedTags from localStorage");
      }
    }
  }, [userId]);

  useEffect(() => {
    if (userId && selectedTags.length > 0) {
      localStorage.setItem(
        `selectedTags-${userId}`,
        JSON.stringify(selectedTags)
      );
    }
  }, [selectedTags, userId]);

  useEffect(() => {
    // Add an event listener for the custom event
    const handleRefreshJobs = () => fetchJobs();
    window.addEventListener("refreshJobs", handleRefreshJobs);

    // Clean up the event listener when component unmounts
    return () => {
      window.removeEventListener("refreshJobs", handleRefreshJobs);
    };
  }, []);

  const fetchTags = () => {
    fetch(`${API_BASE_URL}/backend/api/JobsPage/fetchTags.php?userId=${userId}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          const newTags = data.tags.map((tag) => ({
            id: tag.id,
            name: tag.name,
          }));
          setCustomTags(newTags); // ✅ Store custom tags in state
          setColumns((prevColumns) => [...prevColumns, ...newTags]); // ✅ Ensure new columns are added
        }
      })
      .catch((err) => console.error("Error fetching tags:", err));
  };

  const addColumn = () => {
    if (newColumn.trim()) {
      setColumns([...columns, { id: uuidv4(), name: newColumn.trim() }]);
      setNewColumn("");
    }
  };

  const handleSearch = (query, filterBoard = false) => {
    setSearchQuery(query);

    if (!query.trim()) {
      setSearchResults([]);
      // Reset filtered view
      fetchJobs();
      return;
    }

    if (filterBoard) {
      // Make API call to fetch filtered jobs when search icon is clicked
      fetch(
        `${API_BASE_URL}/backend/api/JobsPage/selectiveSearchJobs.php?userId=${userId}&keyword=${encodeURIComponent(
          query
        )}`
      )
        .then((res) => res.json())
        .then((data) => {
          if (data.status === "success") {
            setTasks(data.jobs); // Replace all tasks with filtered results
            setSearchResults([]); // Hide dropdown
          } else {
            console.error("Error searching jobs:", data.message);
          }
        })
        .catch((err) => console.error("Error searching jobs:", err));
    } else {
      // Local filtering for dropdown results when typing
      const filteredResults = tasks.filter((task) => {
        const companyName = (task.details.CompanyName || "").toLowerCase();
        const jobPosition = (task.details.JobPosition || "").toLowerCase();

        return (
          companyName.includes(query.toLowerCase()) ||
          jobPosition.includes(query.toLowerCase())
        );
      });

      setSearchResults(filteredResults);
    }
  };

  const resetSearch = () => {
    setSearchQuery("");
    setSearchResults([]);
    fetchJobs(); // Restore all jobs
  };

  // Handler for clicking search result
  const handleSearchResultClick = (job) => {
    openViewJobModal(job);
    setSearchResults([]); // Clear results after clicking
    setSearchQuery(""); // Clear search input
  };

  const openTaskModal = (columnId) => {
    setCurrentColumnId(columnId);
    setIsModalOpen(true);
  };

  const openViewJobModal = (job) => {
    console.log("Opening view job modal for:", job);
    setSelectedJob({
      id: job.id,
      title: job.details.CompanyName,
      job_position: job.details.JobPosition,
      event_date: job.details.JobDate,
      event_time: job.details.JobTime,
      location: job.details.JobLocation,
      description: job.details.JobDescription,
      link: job.details.joblink,
    });
    setViewJobModalOpen(true);
  };

  const openEditJobModal = (job) => {
    console.log("Opening edit job modal for:", job);
    setSelectedJob(job);
    setViewJobModalOpen(false); // Close view modal
    setEditJobModalOpen(true); // Open edit modal
  };

  const handleTaskDetailsChange = (e) => {
    const { name, value } = e.target;
    setNewTaskDetails((prevDetails) => ({ ...prevDetails, [name]: value }));
  };

  const fetchJobs = () => {
    if (userId) {
      fetch(
        `${API_BASE_URL}/backend/api/JobsPage/fetchJobs.php?userId=${userId}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      )
        .then((res) => res.json())
        .then((data) => {
          if (data.status === "success") {
            setTasks(data.jobs);
          } else {
            console.error("Error fetching jobs:", data.message);
          }
        })
        .catch((err) => console.error("Error fetching jobs:", err));
    }
  };

  const refreshTasks = () => {
    fetchJobs();
  };

  const handleTaskSubmit = async (newEvent) => {
    if (!userId) {
      alert("User is not logged in!");
      return;
    }

    const selectedColumnId = newEvent.job_category || "saved_jobs";

    const newTask = {
      id: uuidv4(),
      columnId: selectedColumnId, // ✅ Ensure this is correctly passed
      details: {
        userId,
        CompanyName: newEvent.title || "",
        JobPosition: newEvent.job_position || "",
        JobLocation: newEvent.location || "",
        JobDate: newEvent.job_date || null,
        JobTime: newEvent.job_time || null,
        JobDescription: newEvent.description || "",
        joblink: newEvent.link || "",
      },
    };

    // ✅ Debugging: Log request data before sending
    console.log("Sending job data:", JSON.stringify(newTask));

    setTasks([...tasks, newTask]);
    setIsModalOpen(false);
    setNewTaskDetails({
      CompanyName: "",
      JobPosition: "",
      JobLocation: "",
      JobDescription: "",
      JobDate: "",
      JobTime: "",
      joblink: "",
    });

    try {
      const response = await fetch(
        `${API_BASE_URL}/backend/api/JobsPage/addjob.php`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(newTask), // ✅ Ensure the entire object is passed
        }
      );

      const result = await response.json();
      console.log("Server Response:", result); // ✅ Debugging

      if (result.status === "success") {
        alert("Task added successfully!");
        fetchJobs();
      } else {
        console.error("Server error:", result.message);
        alert(result.message);
      }
    } catch (error) {
      console.error("Error adding task:", error);
      alert("An error occurred while adding the task.");
    }
  };

  const handleJobUpdate = async (updatedJob) => {
    if (!userId) {
      alert("User is not logged in!");
      return;
    }

    console.log("Updating job:", updatedJob);

    const taskToUpdate = tasks.find((task) => task.id === updatedJob.id);
    if (!taskToUpdate) {
      console.error("Task not found for updating");
      return;
    }

    // Format date properly for the backend
    let formattedDate = updatedJob.event_date;

    // If date is provided but not in YYYY-MM-DD format
    if (formattedDate) {
      // Check if it's already in YYYY-MM-DD format
      if (!formattedDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // Try to convert from DD/MM/YYYY or MM/DD/YYYY to YYYY-MM-DD
        const dateParts = formattedDate.split(/[\/\-\.]/);
        if (dateParts.length === 3) {
          // If day/month/year format (assuming first is day, second is month)
          if (dateParts[0].length <= 2 && dateParts[1].length <= 2) {
            formattedDate = `${dateParts[2]}-${dateParts[1].padStart(
              2,
              "0"
            )}-${dateParts[0].padStart(2, "0")}`;
          }
        }
      }
    }

    // Format time properly (ensure HH:MM format)
    let formattedTime = updatedJob.event_time;
    if (formattedTime) {
      // If it contains AM/PM, convert to 24-hour format
      if (
        formattedTime.toLowerCase().includes("am") ||
        formattedTime.toLowerCase().includes("pm")
      ) {
        const [timePart, ampm] = formattedTime.split(/\s+/);
        const [hours, minutes] = timePart.split(":").map(Number);

        let hour24 = hours;
        if (ampm.toLowerCase() === "pm" && hours < 12) {
          hour24 += 12;
        } else if (ampm.toLowerCase() === "am" && hours === 12) {
          hour24 = 0;
        }

        formattedTime = `${hour24.toString().padStart(2, "0")}:${minutes
          .toString()
          .padStart(2, "0")}`;
      }
    }

    console.log("Formatted date for server:", formattedDate);
    console.log("Formatted time for server:", formattedTime);

    const updatedDetails = {
      userId,
      CompanyName: updatedJob.title || "",
      JobPosition: updatedJob.job_position || "",
      JobLocation: updatedJob.location || "",
      JobDate: formattedDate || null,
      JobTime: formattedTime || null,
      JobDescription: updatedJob.description || "",
      joblink: updatedJob.link || "",
    };

    // Optimistically update the UI
    setTasks(
      tasks.map((task) =>
        task.id === updatedJob.id
          ? {
              ...task,
              details: {
                ...task.details,
                CompanyName: updatedDetails.CompanyName,
                JobPosition: updatedDetails.JobPosition,
                JobLocation: updatedDetails.JobLocation,
                JobDate: updatedDetails.JobDate,
                JobTime: updatedDetails.JobTime,
                JobDescription: updatedDetails.JobDescription,
                joblink: updatedDetails.joblink,
              },
            }
          : task
      )
    );

    setEditJobModalOpen(false);

    try {
      console.log("Sending update payload:", {
        taskId: parseInt(updatedJob.id),
        userId,
        details: updatedDetails,
        columnId: taskToUpdate.columnId,
      });

      const response = await fetch(
        `${API_BASE_URL}/backend/api/JobsPage/updatejob.php`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            taskId: parseInt(updatedJob.id),
            userId,
            details: updatedDetails,
          }),
        }
      );

      const result = await response.json();
      console.log("Server update response:", result);

      if (result.status === "success") {
        alert("Job updated successfully!");
        fetchJobs(); // Refresh data to ensure consistency
      } else {
        console.error("Server error during update:", result.message);
        alert(result.message);
        fetchJobs(); // Restore data in case of failure
      }
    } catch (error) {
      console.error("Error updating job:", error);
      alert("An error occurred while updating the job.");
      fetchJobs(); // Restore data in case of failure
    }
  };

  const moveTask = async (taskId, newColumnId) => {
    // ✅ Find the correct column name using customTags
    const matchingTag = customTags.find((tag) => tag.id === newColumnId);
    const correctColumnId = matchingTag ? matchingTag.name : newColumnId; // ✅ Use tag name instead of ID

    setTasks(
      tasks.map((task) =>
        task.id === taskId ? { ...task, columnId: correctColumnId } : task
      )
    );

    try {
      const response = await fetch(
        `${API_BASE_URL}/backend/api/JobsPage/updatejob.php`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            taskId,
            userId,
            columnId: correctColumnId, // ✅ Send correct columnId (tag name)
          }),
        }
      );

      const result = await response.json();
      if (result.status !== "success") {
        console.error("Server error during update:", result.message);
        alert(result.message);
        fetchJobs(); // Restore if update fails
      }
    } catch (error) {
      console.error("Error updating job:", error);
      alert("An error occurred while updating the job.");
      fetchJobs(); // Restore if error occurs
    }
  };

  const deleteTask = async (taskId) => {
    console.log("Deleting Task ID:", taskId);

    if (!taskId) {
      console.error("No task ID provided for deletion");
      return;
    }

    try {
      // First update the UI immediately for better user experience
      const updatedTasks = tasks.filter((task) => task.id !== taskId);
      setTasks(updatedTasks);

      // Close the modal
      setViewJobModalOpen(false);

      const response = await fetch(
        `${API_BASE_URL}/backend/api/JobsPage/deletejob.php`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            taskId: typeof taskId === "string" ? parseInt(taskId, 10) : taskId,
            userId,
          }),
        }
      );

      const result = await response.json();
      if (result.status === "success") {
        console.log("Task deleted successfully on the server");
        alert("Task deleted successfully!");
      } else {
        console.error("Server error during deletion:", result.message);
        alert(result.message);
        // Restore the task in the UI if server deletion failed
        fetchJobs();
      }
    } catch (error) {
      console.error("Error deleting task:", error);
      alert("An error occurred while deleting the task.");
      // Restore the task in the UI if server deletion failed
      fetchJobs();
    }
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <KanbanBoardTwo
        selectedTags={selectedTags}
        setSelectedTags={setSelectedTags}
        columns={columns}
        setColumns={setColumns}
        customTags={customTags}
        setCustomTags={setCustomTags}
        onSearch={handleSearch}
        searchResults={searchResults}
        onResultClick={handleSearchResultClick}
        resetSearch={resetSearch}
      />

      {/* Add search bar above kanban board */}
      {/* <SearchBar
        onSearch={handleSearch}
        searchResults={searchResults}
        onResultClick={handleSearchResultClick}
      /> */}
      <div className="kanban-board">
        {columns.map((column) =>
          selectedTags.includes(column.id) ? (
            <KanbanColumn
              key={column.id}
              column={column}
              tasks={tasks.filter((task) => {
                if (task.columnId === column.id) return true;
                const matchingTag = customTags.find(
                  (tag) => tag.name === task.columnId
                );
                return matchingTag && matchingTag.id === column.id;
              })}
              moveTask={moveTask}
              openTaskModal={openTaskModal}
              deleteTask={deleteTask}
              openViewJobModal={openViewJobModal}
              refreshTasks={refreshTasks}
            />
          ) : null
        )}
      </div>
      <div className="floating-add-button">
        <button onClick={() => setIsModalOpen(true)}>
          <AddIcon />
        </button>
      </div>

      {isModalOpen && (
        <AddJobModal
          onAddEvent={handleTaskSubmit}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          customTags={customTags}
        />
      )}

      <ViewJobModal
        isOpen={viewJobModalOpen}
        event={selectedJob}
        onClose={() => setViewJobModalOpen(false)}
        onDelete={deleteTask}
        onEdit={openEditJobModal}
      />

      {editJobModalOpen && (
        <EditJobModal
          isOpen={editJobModalOpen}
          jobData={selectedJob}
          onClose={() => setEditJobModalOpen(false)}
          onSave={handleJobUpdate}
        />
      )}
    </DndProvider>
  );
};

const KanbanColumn = ({
  column,
  tasks,
  moveTask,
  openTaskModal,
  deleteTask,
  openViewJobModal,
  refreshTasks,
}) => {
  const [, drop] = useDrop({
    accept: ItemType,
    drop: (item) => moveTask(item.id, column.id),
  });

  // Sort tasks - favorites first
  const sortedTasks = [...tasks].sort((a, b) => {
    // Sort by favorite status first (favorites on top)
    if (
      (a.details.isFavorite && !b.details.isFavorite) ||
      (a.details.isFavorite && b.details.isFavorite === undefined)
    ) {
      return -1;
    }
    if (
      (!a.details.isFavorite && b.details.isFavorite) ||
      (a.details.isFavorite === undefined && b.details.isFavorite)
    ) {
      return 1;
    }

    // If both have same favorite status, maintain original order
    return 0;
  });

  return (
    <div ref={drop} className="kanban-column">
      <div className="flex justify-between items-center mb-2">
        <h3 className="font-bold">{column.name}</h3>
      </div>

      {sortedTasks.map((task) => (
        <KanbanTask
          key={task.id}
          task={task}
          deleteTask={deleteTask}
          openViewJobModal={openViewJobModal}
          refreshTasks={refreshTasks}
        />
      ))}
    </div>
  );
};

const KanbanTask = ({ task, deleteTask, openViewJobModal, refreshTasks }) => {
  const [{ isDragging }, drag] = useDrag({
    type: ItemType,
    item: { id: task.id },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  });

  const { userId } = useAuth();
  const API_BASE_URL = process.env.REACT_APP_API_SOURCE;
  const [isFavorite, setIsFavorite] = useState(
    task.details.isFavorite || false
  );

  const handleCardClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log("Card clicked:", task.id);
    openViewJobModal(task);
  };

  const toggleFavorite = async (e) => {
    e.stopPropagation(); // Prevent card click

    const newFavoriteStatus = !isFavorite;
    setIsFavorite(newFavoriteStatus); // Update UI immediately

    try {
      const response = await fetch(
        `${API_BASE_URL}/backend/api/JobsPage/updatejob.php`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            taskId: task.id,
            userId,
            details: {
              ...task.details,
              isFavorite: newFavoriteStatus,
            },
          }),
        }
      );

      const result = await response.json();

      console.log("Server update response:", result);
      if (result.status === "success") {
        refreshTasks(); // Call the passed-down refresh function
      } else {
        console.error("Failed to update favorite status:", result.message);
        refreshTasks(); // load data again to ensure consistency
        setIsFavorite(!newFavoriteStatus); // Revert UI if failed
      }
    } catch (error) {
      console.error("Error updating favorite status:", error);
      setIsFavorite(!newFavoriteStatus); // Revert UI if error
      refreshTasks();
    }
  };

  // Format date if it exists
  const formatDate = (dateStr, timeStr) => {
    if (!dateStr) return "";

    try {
      // Parse the date
      const dateParts = dateStr.split("-");
      if (dateParts.length !== 3) return dateStr;

      const formattedDate = `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}`;

      // Add time if available
      if (timeStr) {
        // Parse time to show AM/PM format
        const timeParts = timeStr.split(":");
        if (timeParts.length < 2) return `${formattedDate}, ${timeStr}`;

        let hours = parseInt(timeParts[0]);
        const minutes = timeParts[1];
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12;
        hours = hours ? hours : 12; // Convert 0 to 12

        return `${formattedDate}, ${hours}:${minutes} ${ampm}`;
      }

      return formattedDate;
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateStr;
    }
  };

  return (
    <div
      ref={drag}
      className={`kanban-task ${isDragging ? "opacity-50" : ""} cursor-pointer`}
      onClick={handleCardClick}
    >
      <div className="task-content">
        <div className="job-header">
          <div className="job-position-container">
            <div className="job-position-text">{task.details.JobPosition}</div>
          </div>
          <div className="favorite-icon-container">
            <StarIcon
              className="favorite-icon"
              onClick={toggleFavorite}
              style={{
                color: isFavorite ? "#FFD700" : "#aaaaaa",
                cursor: "pointer",
                fontSize: "18px",
              }}
            />
          </div>
        </div>
        <div className="company-name">{task.details.CompanyName}</div>
        {task.details.JobDate && (
          <div className="job-date">
            {formatDate(task.details.JobDate, task.details.JobTime)}
          </div>
        )}
      </div>
    </div>
  );
};

export default KanbanBoard;
